import type {
  SalaryNotice,
  WorkerSalaryNoticeAdditionalRowResponse,
} from "model/Salary";

/** Backend row-config keys persisted for supported salary adjustments. */
export enum SalaryAdjustmentDatabaseRowKey {
  SalaryAdjustment = 3000,
  Benefit = 2004,
  WageGarnishment = 3001,
}

export const MAX_SALARY_ADJUSTMENT_DIGITS = 9;

const SALARY_ADJUSTMENT_INPUT_PATTERN = new RegExp(
  `^[+-]?\\d{0,${MAX_SALARY_ADJUSTMENT_DIGITS}}$`,
);
const UNSIGNED_SALARY_ADJUSTMENT_INPUT_PATTERN = new RegExp(
  `^\\d{0,${MAX_SALARY_ADJUSTMENT_DIGITS}}$`,
);
const SIGNED_SALARY_ADJUSTMENT_VALUE_PATTERN = new RegExp(
  `^[+-]\\d{1,${MAX_SALARY_ADJUSTMENT_DIGITS}}$`,
);
const UNSIGNED_SALARY_ADJUSTMENT_VALUE_PATTERN = new RegExp(
  `^\\d{1,${MAX_SALARY_ADJUSTMENT_DIGITS}}$`,
);

interface SalaryAdjustmentRule {
  readonly inputPattern: RegExp;
  readonly normalize: (value: number) => number;
  readonly toInputValue: (value: number) => string;
  readonly valuePattern: RegExp;
}

const SALARY_ADJUSTMENT_RULES: Record<
  SalaryAdjustmentDatabaseRowKey,
  SalaryAdjustmentRule
> = {
  [SalaryAdjustmentDatabaseRowKey.SalaryAdjustment]: {
    inputPattern: SALARY_ADJUSTMENT_INPUT_PATTERN,
    normalize: (value) => value,
    toInputValue: (value) => (value > 0 ? `+${value}` : String(value)),
    valuePattern: SIGNED_SALARY_ADJUSTMENT_VALUE_PATTERN,
  },
  [SalaryAdjustmentDatabaseRowKey.Benefit]: {
    inputPattern: UNSIGNED_SALARY_ADJUSTMENT_INPUT_PATTERN,
    normalize: Math.abs,
    toInputValue: (value) => String(Math.abs(value)),
    valuePattern: UNSIGNED_SALARY_ADJUSTMENT_VALUE_PATTERN,
  },
  [SalaryAdjustmentDatabaseRowKey.WageGarnishment]: {
    inputPattern: UNSIGNED_SALARY_ADJUSTMENT_INPUT_PATTERN,
    normalize: (value) => -Math.abs(value),
    toInputValue: (value) => String(Math.abs(value)),
    valuePattern: UNSIGNED_SALARY_ADJUSTMENT_VALUE_PATTERN,
  },
};

export const SALARY_ADJUSTMENT_DATABASE_ROW_KEYS = [
  SalaryAdjustmentDatabaseRowKey.SalaryAdjustment,
  SalaryAdjustmentDatabaseRowKey.Benefit,
  SalaryAdjustmentDatabaseRowKey.WageGarnishment,
] as const;

export interface SalaryAdjustment {
  readonly key: SalaryAdjustmentDatabaseRowKey;
  readonly value: number;
}

function hasAdditionalRowKey({
  key,
  row,
}: {
  readonly key: SalaryAdjustmentDatabaseRowKey;
  readonly row: WorkerSalaryNoticeAdditionalRowResponse;
}): boolean {
  return row.key === key || row.rowConfig?.key === key;
}

function normalizeSalaryAdjustment({
  key,
  value,
}: SalaryAdjustment): SalaryAdjustment {
  return { key, value: SALARY_ADJUSTMENT_RULES[key].normalize(value) };
}

export function isSalaryAdjustmentKey(
  key: number,
): key is SalaryAdjustmentDatabaseRowKey {
  return SALARY_ADJUSTMENT_DATABASE_ROW_KEYS.some(
    (adjustmentKey) => adjustmentKey === key,
  );
}

export function getSalaryAdjustments(
  salary: SalaryNotice,
): readonly SalaryAdjustment[] {
  return SALARY_ADJUSTMENT_DATABASE_ROW_KEYS.flatMap((key) => {
    const row = salary.additionalRows?.find(
      (additionalRow) =>
        additionalRow != null &&
        hasAdditionalRowKey({ key, row: additionalRow }),
    );

    return !row || row.value === 0
      ? []
      : [normalizeSalaryAdjustment({ key, value: row.value })];
  });
}

export function isValidSalaryAdjustmentInput({
  key,
  value,
}: {
  readonly key: SalaryAdjustmentDatabaseRowKey;
  readonly value: string;
}): boolean {
  return SALARY_ADJUSTMENT_RULES[key].inputPattern.test(value);
}

export function getSignedSalaryAdjustmentValue({
  key,
  value,
}: {
  readonly key: SalaryAdjustmentDatabaseRowKey;
  readonly value: string;
}): number | undefined {
  if (!SALARY_ADJUSTMENT_RULES[key].valuePattern.test(value)) return undefined;

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue === 0) return undefined;
  return normalizeSalaryAdjustment({ key, value: numericValue }).value;
}

export function getSalaryAdjustmentInputValue(
  adjustment: SalaryAdjustment,
): string {
  return SALARY_ADJUSTMENT_RULES[adjustment.key].toInputValue(adjustment.value);
}

export function upsertSalaryAdjustment({
  adjustment,
  adjustments,
}: {
  readonly adjustment: SalaryAdjustment;
  readonly adjustments: readonly SalaryAdjustment[];
}): readonly SalaryAdjustment[] {
  const normalizedAdjustment = normalizeSalaryAdjustment(adjustment);
  const nextAdjustments = adjustments.filter(
    (currentAdjustment) => currentAdjustment.key !== adjustment.key,
  );

  return SALARY_ADJUSTMENT_DATABASE_ROW_KEYS.flatMap((key) => {
    const matchingAdjustment =
      key === adjustment.key
        ? normalizedAdjustment
        : nextAdjustments.find(
            (currentAdjustment) => currentAdjustment.key === key,
          );
    return matchingAdjustment ? [matchingAdjustment] : [];
  });
}

export function removeSalaryAdjustment({
  adjustments,
  key,
}: {
  readonly adjustments: readonly SalaryAdjustment[];
  readonly key: SalaryAdjustmentDatabaseRowKey;
}): readonly SalaryAdjustment[] {
  return adjustments.filter((adjustment) => adjustment.key !== key);
}

export function getSalaryAdjustmentMutations({
  adjustments,
  initialAdjustments,
}: {
  readonly adjustments: readonly SalaryAdjustment[];
  readonly initialAdjustments: readonly SalaryAdjustment[];
}): readonly SalaryAdjustment[] {
  const initialByKey = new Map(
    initialAdjustments.map((adjustment) => [
      adjustment.key,
      normalizeSalaryAdjustment(adjustment).value,
    ]),
  );
  const currentByKey = new Map(
    adjustments.map((adjustment) => [
      adjustment.key,
      normalizeSalaryAdjustment(adjustment).value,
    ]),
  );

  return SALARY_ADJUSTMENT_DATABASE_ROW_KEYS.flatMap((key) => {
    const initialValue = initialByKey.get(key);
    const currentValue = currentByKey.get(key);
    if (initialValue === currentValue) return [];
    return [{ key, value: currentValue ?? 0 }];
  });
}
